/**
 * useDialog — promise-based, brand-coherent replacement for the trio of
 * native browser dialogs (`window.confirm` / `window.alert` /
 * `window.prompt`). May 31, 2026.
 *
 * Three APIs:
 *
 *   const ok = await confirm({ title, description, destructive });
 *   await alert({ title, description });
 *   const text = await prompt({ title, label, initialValue });
 *
 * All three return a Promise — caller `await`s. The dialog renders
 * inside the global `<AndoDialogProvider>` mounted at the route shell.
 *
 * Why a Zustand store and not a Context provider? Two reasons:
 *
 *   1. **Callable from anywhere**. Hooks, utility functions, websocket
 *      handlers, store middleware — any module can `import { confirm }`
 *      and prompt the user. Context-only APIs force callers to be inside
 *      React render trees.
 *
 *   2. **Single mount point** keeps the AlertDialog Radix portal singular
 *      — no risk of nested overlays trapping focus on each other.
 *
 * Queueing: when a second dialog opens while one is already shown, the
 * new request goes to the queue and renders after the active one
 * resolves. The queue is intentionally tiny (1 at a time): if you find
 * yourself stacking confirms, the UX is wrong, not the queue.
 */

import { create } from "zustand";

export type DialogKind = "confirm" | "alert" | "prompt";

interface BaseConfig {
  title: string;
  description?: string;
  /** Brand-orange CTA copy. Defaults: "OK" (alert/prompt), "Conferma" (confirm). */
  confirmLabel?: string;
  /** Cancel button copy. Default "Annulla". Hidden on `alert`. */
  cancelLabel?: string;
  /** Render the confirm CTA in red — for irreversible destructive actions. */
  destructive?: boolean;
}

export interface ConfirmConfig extends BaseConfig {
  kind: "confirm";
}

export interface AlertConfig extends BaseConfig {
  kind: "alert";
}

export interface PromptConfig extends BaseConfig {
  kind: "prompt";
  /** Floating label above the input field. */
  label?: string;
  /** HTML `placeholder` on the input. */
  placeholder?: string;
  /** Prefilled value when the dialog opens. */
  initialValue?: string;
  /** Input type — defaults to "text". */
  inputType?: "text" | "email" | "url" | "number";
  /** Min length (caller validates the rest). Confirm button stays disabled below. */
  minLength?: number;
}

export type DialogConfig = ConfirmConfig | AlertConfig | PromptConfig;

type DialogResult = boolean | string | null;

interface ActiveDialog {
  id: number;
  config: DialogConfig;
  resolve: (result: DialogResult) => void;
}

interface DialogStore {
  active: ActiveDialog | null;
  queue: ActiveDialog[];
  /** Schedule a dialog. Returns a Promise that resolves on user action. */
  enqueue: (config: DialogConfig) => Promise<DialogResult>;
  /** Close the active dialog with a result. Called by the renderer. */
  resolve: (result: DialogResult) => void;
}

let nextId = 1;

// NOTE: zustand types aren't transitively reachable from ``@ando/ui``
// (zustand sits only on ``@ando/client``'s dep list — runtime resolution
// works via pnpm hoist, but TS doesn't follow). Annotate ``set``/``get``
// explicitly so the inferred ``any`` doesn't trip ``noImplicitAny``.
type _Set = (
  partial:
    | Partial<DialogStore>
    | ((s: DialogStore) => Partial<DialogStore>),
) => void;
type _Get = () => DialogStore;

export const useDialogStore = create<DialogStore>((set: _Set, get: _Get) => ({
  active: null,
  queue: [],
  enqueue: (config: DialogConfig) =>
    new Promise<DialogResult>((resolve) => {
      const entry: ActiveDialog = { id: nextId++, config, resolve };
      set((s: DialogStore) => {
        if (s.active) return { queue: [...s.queue, entry] };
        return { active: entry };
      });
    }),
  resolve: (result: DialogResult) => {
    const { active, queue } = get();
    if (!active) return;
    active.resolve(result);
    const [next, ...rest] = queue;
    set({ active: next ?? null, queue: rest });
  },
}));

// ─── Public, importable-from-anywhere API ──────────────────────────────────

export interface ConfirmOptions extends Omit<ConfirmConfig, "kind"> {}
export interface AlertOptions extends Omit<AlertConfig, "kind"> {}
export interface PromptOptions extends Omit<PromptConfig, "kind"> {}

/** Brand-coherent replacement for ``window.confirm``. Returns `true` on
 *  the confirm button, `false` on cancel/ESC/backdrop click. */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return useDialogStore
    .getState()
    .enqueue({ kind: "confirm", ...options })
    .then((r: unknown) => r === true);
}

/** Brand-coherent replacement for ``window.alert``. Resolves when the
 *  user dismisses the dialog. Always resolves to `undefined`. */
export function alert(options: AlertOptions): Promise<void> {
  return useDialogStore
    .getState()
    .enqueue({ kind: "alert", ...options })
    .then(() => undefined);
}

/** Brand-coherent replacement for ``window.prompt``. Returns the entered
 *  string on confirm, `null` on cancel. */
export function prompt(options: PromptOptions): Promise<string | null> {
  return useDialogStore
    .getState()
    .enqueue({ kind: "prompt", ...options })
    .then((r: unknown) => (typeof r === "string" ? r : null));
}

// ─── Hook for React components that want the same surface ──────────────────

export function useDialog(): {
  confirm: typeof confirm;
  alert: typeof alert;
  prompt: typeof prompt;
} {
  return { confirm, alert, prompt };
}
